import { useEffect, useMemo, useRef, useState } from 'react';
import { FormControl, MenuItem, Select, Stack } from '@mui/material';
import dayjs from 'dayjs';

import {
  ActionItem,
  LeaseLifecycle,
  Unit,
  UserData,
} from '@urbanmix-tech/shared-js';

import {
  convertPseudoDateToRealDate,
  getLongNumberStr,
} from '@/helpers/common';

import {
  ITableCell,
  ITableRow,
  VirtualizedTable,
  ColumnSeletion,
} from '@/components/shared/Table';

// Redux
import { useDispatch } from 'react-redux';
import {
  setHighlightedUnits,
  setSelectedUnit,
  setApartmentData,
  setVisualizer,
  setHoveredUnitId,
  setBuildingVisual,
} from '@/features/project/projectSlice';
import {
  ColorGradeVisualizer,
  ColorGradientVisualizer,
  Visualizer,
} from '@urbanmix-tech/um-embedded';

import { GenericDistinctVisualizer, colorScales } from './BuildingVisualizers';
import type { ColorScaleOptions } from './BuildingVisualizers';

const steps: Record<string, number> = {
  waiting_to_send: 1,
  waiting_for_response: 2,
  waiting_for_tenant_signature: 3,
  waiting_for_countersignature: 4,
  upload_lease: 5,
};

let stepsNum: Record<number, string> = {};

for (const key in steps) {
  const value = steps[key];
  stepsNum[value] = key;
}

let stepToStatus: Record<number, string> = {
  1: 'Started',
  2: 'Sent to tenant',
  3: 'Tenant signed',
  4: 'Completed',
  5: 'Completed',
};

type LeasingTabContentsProps = {
  units: Unit[];
  actionItems: ActionItem[];
  hoveredUnitId: string | null;
  onSelectUnit(unitId: string | null): void;
  onHoverUnit(unitId: string | null): void;
};

export function LeasingTabContent({
  units,
  actionItems,
  hoveredUnitId,
  onSelectUnit,
  onHoverUnit,
}: LeasingTabContentsProps) {
  const ref = useRef<HTMLDivElement>(null);

  // Mount Redux dispatcher
  const dispatch = useDispatch();

  const handleClickRow = (apt: any) => {
    console.log('handle click row', apt);
    dispatch(setSelectedUnit({ apartmentId: apt.id }));
  };

  const columns: ITableCell[] = useMemo(
    () => [
      {
        label: 'Unit',
        sortable: true,
        className: 'bg-gray-950',
      },
      {
        label: 'Type',
        sortable: true,
        className: 'bg-gray-950',
        showCheckbox: true,
      },
      {
        label: 'Status',
        sortable: true,
        className: 'bg-gray-950',
        showCheckbox: true,
      },
      {
        label: 'Days',
        sortable: true,
        className: 'bg-gray-950',
        showCheckbox: true,
      },
      {
        label: 'Rent',
        sortable: true,
        className: 'bg-gray-950',
        showCheckbox: true,
      },
      {
        label: 'Concessions',
        sortable: true,
        className: 'bg-gray-950',
        showCheckbox: true,
      },
    ],
    []
  );

  //   useEffect(() => {
  //     // for each unit, go through all actionItems and aggregate
  //     let unitsData = units.map((unit) => {
  //         let unitActionItems = actionItems.filter((actionItem) => {
  //           return actionItem.unitId === unit.id;
  //         })

  //         const lastStep = unitActionItems.reduce((acc, item) => {
  //             return Math.max(acc, steps[item.status]);
  //             }, 0);

  //         const firstDate = unitActionItems.reduce((acc, item) => {
  //             if (item.response && item.response.event && steps[item.response.event]) {
  //                 //@ts-ignore
  //                 return Math.min(acc, item.createDate._seconds);
  //             } else return acc;
  //             }, Infinity);

  //         const lastDate = unitActionItems.reduce((acc, item) => {
  //             if (item.response && item.response.event && steps[item.response.event]) {
  //                 //@ts-ignore
  //                 return Math.max(acc, item.completedOn?._seconds);
  //             } else return acc;
  //             }, 0);

  //         const days = lastDate - firstDate / 60 / 60 / 24;

  //         const lastStatus = stepsNum[lastStep];

  //         return {
  //             id: unit.id,
  //             currentState: lastStatus,
  //             days: days,
  //         }
  //     })
  //   },[])

  const rows: ITableRow[] = useMemo(() => {
    let unitsData = units.map((unit) => {
      let unitActionItems = actionItems.filter((actionItem) => {
        return actionItem.unitId === unit.id;
      });

      if (unitActionItems.length === 0) {
        return {
          id: unit.id,
          days: 0,
          actions: 0,
        };
      }

      const lastStep = unitActionItems.reduce((acc, item) => {
        if (
          item.response &&
          item.response.event &&
          steps[item.response.event] &&
          item.status === 'done'
        ) {
          return Math.max(acc, steps[item.response.event]);
        } else {
          return acc;
        }
      }, 0);
      console.log('unitActionItems', unitActionItems);
      console.log('last step', lastStep);

      const firstDate = unitActionItems.reduce((acc, item) => {
        
        let d = convertPseudoDateToRealDate(item.createDate);
        // console.log('item', item.createDate,d);
        if (d) {
          return Math.min(acc, d.getTime());
        } else { 
          console.log('no date???',item);
          return acc;
        }
      }, Infinity);
      console.log('first date', firstDate);

      let lastDate = unitActionItems.reduce((acc, item) => {
        let d = convertPseudoDateToRealDate(item.createDate);
        if (d && item.status === 'done') {
          return Math.max(acc, d.getTime());
        } else return acc;
      }, -Infinity);
      if (lastDate === -Infinity) {
        console.log('no last date', unitActionItems);
        lastDate = Date.now();
      }      
      

      let sortedItems = unitActionItems.sort((a, b) => {
        //@ts-ignore
        return a.createDate.seconds - b.createDate.seconds;
      });

      let termsItem = sortedItems.reverse().find((item) => {
        return item.response && item.response.leaseTerms;
      });

      if (lastStep < 4) {
        // set last date to now in unix timestmap
        lastDate = Math.floor(Date.now());
      }

      // convert seconds to days
      const days = Math.ceil((lastDate - firstDate) / 1000 / 60 / 60 / 24);
      console.log('days',lastDate,firstDate, days);
      const lastStatus = stepToStatus[lastStep];

      return {
        id: unit.id,
        currentState: lastStatus,
        started: firstDate,
        ended: lastDate,
        days: days,
        actions: unitActionItems.length,
        type: unit.affordable ? 'affordable' : 'market',
        terms: termsItem?.response.leaseTerms,
        concessions: termsItem?.response.concessions,
      };
    });

    return unitsData
      .filter((unitData) => unitData.actions > 1)
      .map((unit) => {
        const data = {
          Status: {
            kind: 'string',
            label: unit.currentState,
            value: unit.currentState,
            className: '',
          },
          Unit: {
            label: unit.id,
            value: unit.id,
          },
          Rent: {
            label: unit.terms
              ? `$${getLongNumberStr(unit.terms.termsMonthlyRent || 0)}`
              : 0,
            value: unit.terms ? unit.terms.termsMonthlyRent : 0,
            className: '',
          },
          Type: {
            label: unit.type,
            value: unit.type,
            className: '',
          },
          Days: {
            label: `${unit.days}`,
            value: unit.days,
            className: '',
          },
          Concessions: {
            label: unit.concessions ? unit.concessions.concessionOf : undefined,
            value: unit.concessions ? unit.concessions.concessionOf : undefined,
            className: '',
          },
        } as Record<string, ITableCell>;

        return {
          id: unit.id,
          data,
        } as ITableRow;
      });
  }, [actionItems, units]);

  const [selectedScale, setSelectedScale] =
    useState<ColorScaleOptions>('default');
  const [selectedColumn, setSelectedColumn] = useState<ColumnSeletion | null>(
    null
  );

  useEffect(() => {
    let isNumbers = true;
    let column = selectedColumn;
    if (!column || column.value === false) {
      dispatch(setApartmentData([]));
      dispatch(setVisualizer(null));
      dispatch(setHighlightedUnits([]));
      return;
    }
    let values = rows.map((row) => {
      if (row.data[column.label]['kind'] === 'string') {
        isNumbers = false;
        return (row.data[column.label].label ?? '') as string;
      }
      return +(row.data[column.label].value ?? 0);
    });

    const distinctValues = new Set(values);
    // const colors = colorScales[selectedScale];
    const colors = ['#DBC9FF','#B390FF','#B390FF','#733AEA'].reverse();

    // let stepsColorMap: Map<string, string> = new Map([
    //   ['Started', '#DBC9FF'],
    //   ['Sent to tenant', '#B390FF'],
    //   ['Tenant signed', '#B390FF'],
    //   ['Completed', '#733AEA'],

    let visualizer = null;
    // if less than 10 distinct values
    if (distinctValues.size < 10) {
      let colorMap = new Map();
      let arrDistinctValues = Array.from(distinctValues);
      arrDistinctValues.forEach((value, index) => {
        colorMap.set(value.toString(), colors[index]);
      });
      console.log('setting visualizer for distinct values', colorMap);
      visualizer = new GenericDistinctVisualizer(colorMap);
    } else {
      if (!isNumbers) {
        visualizer = new ColorGradeVisualizer(colors, '', '');
      } else {
        const minValue = values.reduce((acc, value) => {
          if (value != 0) {
            return Math.min(acc as number, value as number);
          }
          return acc;
        }, Infinity);
        const maxValue = values.reduce((acc, value) => {
          return Math.max(acc as number, value as number);
        }, -Infinity);
        // console.log('setting visualizer for non distinct values', minValue,maxValue);
        // visualizer = new ColorGradientVisualizer(['#313695', '#A50026'], minValue, maxValue);
        visualizer = new ColorGradeVisualizer(colors, minValue, maxValue);
      }
    }

    // aggregate 
    let groups : Record<string,number> = {};

    let data = rows.map((row) => {
      // console.log('row', row.data['Unit'].value,row.data[column.label].label);
      if (!groups[row.data[column.label].label]) {
        groups[row.data[column.label].label] = 0;
      }
      groups[row.data[column.label].label] += 1;
      return {
        apartmentId: row.data['Unit'].value,
        value:
          row.data[column.label].kind == 'string'
            ? row.data[column.label].label
            : row.data[column.label].value,
        label: column.label,
      };
    });

    console.log('groups', groups);
    console.log('units to hihglight', units);

    dispatch(
      setBuildingVisual({
        apartmentData: data,
        visualizer: visualizer,
      })
    );

    //
    setTimeout(() => {
      dispatch(setHighlightedUnits(data));
    }, 500);
  }, [selectedColumn, selectedScale]);

  return (
    <div className="unit-mix h-full w-full" ref={ref}>
      <div className="postion-absoulte right-0 top-0 z-10 mb-6 flex flex-row gap-12">
        <FormControl>
          <Select
            value={selectedScale}
            onChange={(e) => {
              setSelectedScale(e.target.value as ColorScaleOptions);
            }}
            label="Color Scale"
            size="xs"
            variant="standard"
          >
            <MenuItem value="blues">Blues</MenuItem>
            <MenuItem value="default">Green to Maroon</MenuItem>
            <MenuItem value="purpleToYellow">Purple to Yellow</MenuItem>
            <MenuItem value="tealToMagenta">Teal to Magenta</MenuItem>
            <MenuItem value="earthTones">Earth Tones</MenuItem>
            <MenuItem value="rainbow">Rainbow</MenuItem>
            <MenuItem value="pastel">Pastel</MenuItem>
          </Select>
        </FormControl>
      </div>
      <VirtualizedTable        
        rows={rows}
        columns={columns}
        px={16}
        onClickRow={handleClickRow}
        hoveredRowId={hoveredUnitId || undefined}
        onChangeHover={(rowId) => {
          // console.log('on change hover')
          onHoverUnit(rowId);
          dispatch(setHoveredUnitId(rowId));
        }}
        classNames={{          
          container:
            'rounded-none border-0  border-zinc-600 ',
        }}
        onColumnSelected={(column) => {
          setSelectedColumn(column);
        }}
      />
    </div>
  );
}
